import { useEffect, useState } from "react";
import AccountConnection from "pages/AccountConnection";
import { facebookGraphInit } from "functions/facebookAds";
import {
  getUserData,
  setIsExternalApi,
  setIsRebilling,
  setUserData,
  setWorkspaceId,
} from "actions/app";
import store from "store";
import { Oval } from "react-loader-spinner";
import { getSigninToken } from "functions/login";
import { signInWithCustomToken } from "firebase/auth";
import { auth } from "config/firebase";
import FbManagerAccess from "pages/FbManagerAccess";
import GoogleLinkBridge from "pages/GoogleLinkBridge";
import { ValidateExternalLink } from "functions/externalApi";
import ThemeSelector from "components/ThemeSelector";
import {
  ThemeProvider,
  getAccentFromSearchParams,
  getThemeFromSearchParams,
  isAccentId,
  isThemeMode,
} from "theme";

const ConnectPlatforms = () => {
  const [params, setParams] = useState();
  const [connectId, setConnectId] = useState("");
  const [googleConnectId, setGoogleConnectId] = useState("");
  const [workspaceIdFromParam, setWorkspaceIdFromParam] = useState("");
  const [isGettingStarted, setIsGettingStarted] = useState(false);
  const [isValidatingLink, setIsValidatingLink] = useState(false);
  const [isLinkInValid, setIsLinkInValid] = useState(false);
  const urlTheme = getThemeFromSearchParams();
  const urlAccent = getAccentFromSearchParams();

  const handleSigninWithToken = async (
    locationId: string,
    isExternalApi: boolean = false,
  ) => {
    try {
      setIsGettingStarted(true);
      const token: any = await getSigninToken(locationId);
      if (token) {
        await signInWithCustomToken(auth, token);
        store.dispatch(getUserData(locationId, isExternalApi));
        store.dispatch(setWorkspaceId(locationId));
      }
    } catch (error) {
      const errorMessage = (error as any).message;
      console.log(errorMessage, "error message");
    } finally {
      setTimeout(() => {
        setIsGettingStarted(false);
      }, 2000);
    }
  };

  const handleValidateLink = async (link: string) => {
    try {
      setIsValidatingLink(true);
      const isValid: any = await ValidateExternalLink(link);
      setIsLinkInValid(!isValid);
    } catch (error) {
      const errorMessage = (error as any).message;
      console.log(errorMessage, "error message");
    } finally {
      setIsValidatingLink(false);
    }
  };

  useEffect(() => {
    if (window.location.search) {
      const url = new URL(window.location.href);
      console.log(url, "url string");
      const params: any = Object.fromEntries(new URLSearchParams(url.search));
      console.log(params, "params");
      const state =
        typeof params?.state === "string"
          ? JSON.parse(params?.state)
          : params?.state;
      const id: any = params?.id || state?.id;
      const connect_id: any = params?.connect_id || state?.connect_id;
      const google_connect_id: any =
        params?.google_connect_id || state?.google_connect_id;
      // Parent agency workspaceId for agency-client Link Bridge links
      const workspaceIdParam: any =
        params?.workspaceId || state?.workspaceId || "";
      const isRebilling: any = params?.isRebilling;
      const link_id: any = params?.link_id;

      setParams(params);
      if (workspaceIdParam) {
        setWorkspaceIdFromParam(workspaceIdParam);
      }

      if (link_id) {
        handleValidateLink(window.location.href);
        store.dispatch(setIsExternalApi(true));
      } else {
        store.dispatch(setIsExternalApi(false));
      }

      if (id) {
        console.log(auth?.currentUser?.uid, id, "id check");
        if (!auth?.currentUser?.uid || auth?.currentUser?.uid !== id) {
          store.dispatch(setUserData({}));
          handleSigninWithToken(id, link_id ? true : false);
        } else {
          store.dispatch(getUserData(id));
          store.dispatch(setWorkspaceId(id));
        }

        facebookGraphInit();
        store.dispatch(setIsRebilling(isRebilling));
      } else if (connect_id) {
        console.log(connect_id, "asdjkasdhakjsdhajksh");
        setConnectId(connect_id);
        facebookGraphInit();
      } else if (google_connect_id) {
        console.log(connect_id, "asdjkasdhakjsdhajksh");
        setGoogleConnectId(google_connect_id);
      }
    }
  }, []);

  const renderLoadingSpinner = () => (
    <div>
      <Oval
        height={40}
        width={40}
        color={"var(--color-primary)"}
        wrapperStyle={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "100vh",
        }}
        wrapperClass=""
        visible={true}
        ariaLabel="oval-loading"
        secondaryColor="var(--text-grey)"
        strokeWidth={2}
        strokeWidthSecondary={2}
      />
    </div>
  );

  const renderInvalidLink = () => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "400px",
        padding: "24px",
        textAlign: "center",
      }}
    >
      <div style={{ marginBottom: "16px" }}>
        <svg
          style={{
            width: "64px",
            height: "64px",
            color: "#ef4444",
            margin: "0 auto",
          }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>
      <h1
        style={{
          fontSize: "24px",
          fontWeight: 600,
          color: "var(--text-primary)",
          marginBottom: "8px",
        }}
      >
        Invalid Link
      </h1>
      <p style={{ color: "var(--text-secondary)", maxWidth: "28rem" }}>
        The link you're trying to access is invalid or has expired. Please try
        again or contact support if the problem persists.
      </p>
    </div>
  );

  const lockedThemeProps = {
    lockToUrlTheme: true as const,
    initialThemeMode: isThemeMode(urlTheme) ? urlTheme : null,
    initialAccentId: isAccentId(urlAccent) ? urlAccent : null,
  };

  // Connect flows stay locked to web-app URL theme. Screens that show a toggle
  // (e.g. invalid link) must not lock, or ThemeSelector returns null and the
  // appearance bar stays empty.
  const withLockedTheme = (screen: JSX.Element, showThemeToggle = false) => (
    <ThemeProvider
      {...(showThemeToggle
        ? {
            lockToUrlTheme: false,
            initialThemeMode: lockedThemeProps.initialThemeMode,
            initialAccentId: lockedThemeProps.initialAccentId,
          }
        : lockedThemeProps)}
    >
      {showThemeToggle ? (
        <>
          <div className="appearance-bar">
            <ThemeSelector variant="header" />
          </div>
          {screen}
        </>
      ) : (
        screen
      )}
    </ThemeProvider>
  );

  if (connectId) {
    return withLockedTheme(
      <FbManagerAccess
        connectId={connectId}
        workspaceId={workspaceIdFromParam}
      />,
    );
  }

  if (googleConnectId) {
    return withLockedTheme(
      <GoogleLinkBridge
        connectId={googleConnectId}
        workspaceId={workspaceIdFromParam}
      />,
    );
  }

  if (isGettingStarted || !params) {
    return renderLoadingSpinner();
  }

  if (isLinkInValid) {
    return withLockedTheme(renderInvalidLink(), true);
  }

  // Ads connector: lock to theme + accent passed from web-app (or bootstrap default).
  return withLockedTheme(<AccountConnection params={params} />);
};

export default ConnectPlatforms;
